/**
 * Program & Site Service - Fetches and caches program/site records
 *
 * The /programs and /sites endpoints return only the records the current
 * user has access to (scoped by current_person_id). Full records (with names)
 * are stored in IndexedDB for offline use.
 */

import db from '@/db/db';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchWithCache } from '../../lib/api-client';
import { Program, Site } from '../types/user';
import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('ProgramSiteService', LogLevel.DEBUG);

/**
 * Refresh programs from the server and store in IndexedDB
 */
export async function refreshPrograms(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('No current person - skipping programs refresh');
    return;
  }

  const url = `${API_ENDPOINTS.programs}?current_person_id=${currentPerson.id}`;
  const { response, notModified, commit } = await fetchWithCache(url, {
    organizationId: currentPerson.organization.id,
    name: `programs_person_${currentPerson.id}`,
  }, { timeout: 15000 });

  if (notModified) {
    logger.debug('Programs not modified (304)');
    return;
  }

  if (!response.ok) {
    if (response.status === 404) {
      logger.debug('Programs endpoint not available');
      return;
    }
    throw new Error(`Failed to fetch programs: ${response.status}`);
  }

  const data = await response.json();
  const programs: Program[] = Array.isArray(data) ? data : (data.records || data.programs);

  if (!programs || !Array.isArray(programs)) {
    logger.warn('Unexpected programs response format:', data);
    return;
  }

  const orgId = currentPerson.organization.id;

  await db.transaction('rw', db.programs, async () => {
    // Remove old programs for this organization
    await db.programs.where('organization_id').equals(orgId).delete();
    // Insert fresh records
    await db.programs.bulkPut(programs.map(p => ({ ...p, organization_id: orgId })));
  });
  await commit();

  logger.debug(`Refreshed ${programs.length} programs`);
}

/**
 * Refresh sites from the server and store in IndexedDB
 */
export async function refreshSites(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('No current person - skipping sites refresh');
    return;
  }

  const url = `${API_ENDPOINTS.sites}?current_person_id=${currentPerson.id}`;
  const { response, notModified, commit } = await fetchWithCache(url, {
    organizationId: currentPerson.organization.id,
    name: `sites_person_${currentPerson.id}`,
  }, { timeout: 15000 });

  if (notModified) {
    logger.debug('Sites not modified (304)');
    return;
  }

  if (!response.ok) {
    if (response.status === 404) {
      logger.debug('Sites endpoint not available');
      return;
    }
    throw new Error(`Failed to fetch sites: ${response.status}`);
  }

  const data = await response.json();
  const sites: Site[] = Array.isArray(data) ? data : (data.records || data.sites);

  if (!sites || !Array.isArray(sites)) {
    logger.warn('Unexpected sites response format:', data);
    return;
  }

  const orgId = currentPerson.organization.id;

  await db.transaction('rw', db.sites, async () => {
    // Remove old sites for this organization
    await db.sites.where('organization_id').equals(orgId).delete();
    // Insert fresh records
    await db.sites.bulkPut(sites.map(s => ({ ...s, organization_id: orgId })));
  });
  await commit();

  logger.debug(`Refreshed ${sites.length} sites`);
}
